import type { GeoPoint } from './routeMapView'
import type { PlannedLocation } from './routePlanning'

// Mode ajustement de l'écran Routes (demande du propriétaire du 11 oct. 2026) : déplacer une pastille corrige la
// POSITION D'INTERVENTION de l'adresse de service (entrée, stationnement), jamais son adresse, sa route ou son
// ordre. Le serveur fait autorité (version de position); ces fonctions gèrent l'affichage optimiste.

/** Précision enregistrée par le serveur (numeric(9, 6)) : environ 10 cm. */
export function roundCoordinate(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

export function samePosition(left: GeoPoint, right: GeoPoint): boolean {
  return (
    roundCoordinate(left.lat) === roundCoordinate(right.lat) &&
    roundCoordinate(left.lng) === roundCoordinate(right.lng)
  )
}

/**
 * Position affichée en attendant le serveur. `seq` croît à chaque geste sur la même adresse : une réponse plus
 * ancienne que le dernier geste ne touche plus l'affichage. `version` = version serveur connue après succès.
 */
export interface PositionOverride {
  lat: number
  lng: number
  saving: boolean
  seq: number
  version: number | null
  /** Ajustée (et non revenue au géocodage) une fois enregistrée. */
  adjusted: boolean
}

/** Positions effectives à afficher : la projection, corrigée par les déplacements pas encore relus. */
export function withOverrides(
  locations: readonly PlannedLocation[],
  overrides: ReadonlyMap<string, PositionOverride>,
): PlannedLocation[] {
  if (overrides.size === 0) return [...locations]
  return locations.map((location) => {
    const override = overrides.get(location.id)
    return override ? { ...location, latitude: override.lat, longitude: override.lng } : location
  })
}

/** Retire les corrections que la projection a rattrapées (version relue au moins égale). */
export function settleOverrides(
  overrides: ReadonlyMap<string, PositionOverride>,
  locations: readonly PlannedLocation[],
): Map<string, PositionOverride> {
  const versions = new Map(locations.map((location) => [location.id, location.positionVersion]))
  const next = new Map<string, PositionOverride>()
  for (const [id, override] of overrides) {
    const known = versions.get(id)
    const caughtUp =
      !override.saving && override.version !== null && known !== undefined && known >= override.version
    if (!caughtUp) next.set(id, override)
  }
  return next
}

export type UncertainOutcome = 'applied' | 'not-applied' | 'superseded'

/**
 * Réponse réseau incertaine : l'état relu tranche. Version inchangée = rien n'a été écrit; version suivante à la
 * position voulue = écrit; autre chose = quelqu'un d'autre a modifié la position entre-temps.
 */
export function uncertainOutcome(
  location: Pick<PlannedLocation, 'latitude' | 'longitude' | 'positionVersion'> | undefined,
  intended: GeoPoint,
  expectedVersion: number,
): UncertainOutcome {
  if (!location || location.positionVersion === expectedVersion) return 'not-applied'
  if (
    location.positionVersion === expectedVersion + 1 &&
    location.latitude !== null &&
    location.longitude !== null &&
    samePosition({ lat: location.latitude, lng: location.longitude }, intended)
  ) {
    return 'applied'
  }
  return 'superseded'
}
